import React, {
  useRef, useState, useEffect,
} from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import './SelectInputStyles.scss';
import { useDispatch } from 'react-redux';
import { Icons } from '../../base/BootstrapComponents/BootstrapComponents';
import Dropdown from '../../base/Dropdown/Dropdown';
import TextInput from '../TextInput/TextInput';
import { errorNotification } from '../../../modules/Notifications/notificationDecorator';
import { getIsRequired, getResponseError } from '../../../utils/utils';
import { metaDataFormatter, renderRegistryOptionMin } from '../../../utils/formatters';
import { LOADING_DELAY } from '../../../configs';
import TextInputWithLabel from '../TextInput/TextInputWithLabel';
import renderMessage from '../renderMessage';

export default function RegistryNumberSelectorWithTextInput(props) {
  const {
    inputProps = null,
    className = '',
    fieldState = {
      error: '',
      touched: false,
    },
    variant = 'default',
    multiple = false,
    label = '',
    disabled = false,
    defaultOptions = [],
    defaultValues = [],
    dtoObject,
    customValidator = [],
    setRegisterNumberValueCallback = () => {},
    loadAction,
    formMeta,
  } = props;
  const {
    error,
    isTouched: touched,
  } = fieldState;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [toggleFunction, setToggleFunction] = useState(() => () => {});
  const [defaults, setDefaults] = useState(defaultOptions);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [timer, setTimer] = useState(null);
  const anchorElement = useRef(null);
  const inputRef = useRef(null);
  const isError = Boolean(error);
  const value = inputProps && (inputProps.value || typeof inputProps.value === 'boolean') ? inputProps.value : [];
  const [open, setOpen] = useState(false);

  const optionFormatter = item => ({
    value: item.number, name: item.number, applicant: item, number: item.number,
  });

  useEffect(() => {
    if (!loadAction || !defaultValues || !defaultValues.length) {
      loadOptions(value);
      return;
    }
    dispatch(loadAction(defaultValues))
      .then(data => {
        if (!data) { return; }
        setDefaults((data || []).map(optionFormatter));
      })
      .catch(response => errorNotification('Загрузка данных', getResponseError(response)));
  }, [JSON.stringify(defaultValues)]);

  const onChange = item => {
    if (inputProps && inputProps.onChange) {
      setSearch(item.value);
      inputProps.onChange({ target: { value: item.value, name: inputProps.name } });
    }
  };

  const onClear = item => {
    if (inputProps && inputProps.onChange) {
      setSearch('');
      setOptions([]);
      setDefaults([]);
      inputProps.onChange({ target: { value: null, name: inputProps.name } });
    }
  };

  const onChangeTextInput = event => {
    const { value } = event.target;
    inputProps.onChange({ target: { value, name: inputProps.name } });
    setSearch(value);
  };

  const loadOptions = value => {
    dispatch(loadAction(value))
      .then(data => {
        // if (!data) { return; }
        setOptions(((data?.length && data) || [{ number: value }]).map(optionFormatter));
      })
      .catch(response => errorNotification('Загрузка данных', getResponseError(response)));
  };

  const onChangeSearch = event => {
    const { value } = event.target;
    setSearch(value);
    setRegisterNumberValueCallback(value);
    if (!loadAction) return;
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(() => { loadOptions(value); }, LOADING_DELAY));
  };

  const toggleOpen = open => {
    if (disabled) {
      return;
    }
    setOpen(open);
  };

  const handleClose = () => setOpen(false);

  const handleInputChange = event => {
    if (!open) {
      toggleOpen(true);
    }
    onChangeTextInput(event);
    onChangeSearch(event);
  };

  const name = inputProps && (typeof inputProps.name !== 'undefined') ? inputProps.name : '';

  const optionList = [...options, ...defaults.filter(item => !options.find(f => f.value === item.value))];
  const selectedItems = optionList.filter(item => value.includes(item.value));
  const isRequired = getIsRequired(dtoObject, customValidator);
  return (
    <div className={classNames('form-control-wrapper', className)}>
      <Dropdown
        dropBottom
        usePortal
        disabled={disabled}
        onToggleOpen={toggleOpen}
        setToggleFunction={a => setToggleFunction(() => a)}
        customHandleClose={handleClose}
      >
        <Dropdown.Toggle className="select-toggle">
          <div
            ref={anchorElement}
            className={classNames('form-control-wrapper multiple-select', className, { material: variant === 'material' }, { default: variant !== 'material' })}
          >
            <div className={classNames('input-block', { 'is-invalid': isError })}>
              {metaDataFormatter(formMeta, name || '', renderMessage(label))}
              <div className="multiple-value-block">
                {selectedItems.map((item, index) => (
                  <div key={index} className="multiple-value registry-value">
                    {renderRegistryOptionMin(item)}
                    <Icons
                      customIcon
                      className="multiple-value-delete"
                      width={18}
                      height={18}
                      icon="close"
                      onClick={event => {
                        onClear();
                      }}
                    />
                  </div>
                ))}
                <TextInput
                  fieldState={fieldState}
                  containerRef={anchorElement}
                  inputProps={{
                    name,
                    onChange: handleInputChange,
                    value: selectedItems.length ? '' : search,
                    ref: inputRef,
                  }}
                />
              </div>
              <div htmlFor={props.id} className="form-input-label">
                {renderMessage(label)}{isRequired ? <span className="form-label-mark-required">*</span> : null}
              </div>
              <span className="input-after-block">
                <div className="select-arrow-wrapper">
                  <Icons customIcon className="select-arrow" width={12} height={8} icon="arrow-down" />
                </div>
              </span>
            </div>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          key={optionList}
          disableClickToggle={multiple}
          rightPadding={32}
          className="select-options-wrapper"
          anchorElement={anchorElement}
          classNamePortal="portal-select-options multiple-select-option"
          useResizeContent={false}
        >
          {optionList && optionList.length ? (
            <div className="select-options-list">
              {optionList.map((item, key) => {
                const isSelected = !!value.includes(item.value);
                return (
                  <div
                    key={key}
                    className={classNames('select-option', { selected: isSelected })}
                    onClick={event => {
                      onChange(item);
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    {renderRegistryOptionMin(item)}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="select-options-empty-list">
              Список пуст
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
